import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import ResponsiveDialog from '../../components/molecules/responsiveDialog';
import api from '../../services/api';
import { EmailChips } from './emailChips';
import { Title } from '../../components/atoms/title';

export function Rewards() {
  const [emails, setEmails] = useState([]);
  const [formData, setFormData] = useState({});

  const [loading, setLoading] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogTitle, setErrorDialogTitle] = useState('');
  const [errorDialogContent, setErrorDialogContent] = useState('');
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [message, setMessage] = useState('');

  const validationSchema = yup.object().shape({
    super_likes: yup
      .number()
      .transform(value => (isNaN(value) || value === null || value === undefined ? 0 : value))
      .min(0, 'Super Likes cannot be negative')
      .max(10, 'Super Likes cannot be more than 10'),
    unlocks: yup
      .number()
      .transform(value => (isNaN(value) || value === null || value === undefined ? 0 : value))
      .min(0, 'Unlocks cannot be negative')
      .max(10, 'Unlocks cannot be more than 10'),
    ucoins: yup
      .number()
      .transform(value => (isNaN(value) || value === null || value === undefined ? 0 : value))
      .min(0, 'Ucoins cannot be negative')
      .max(1000, 'Ucoins cannot be more than 1000'),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const handleSendItems = async data => {
    const body = {
      emails,
      ucoins: Number(data.ucoins),
      unlocks: Number(data.unlocks),
      super_likes: Number(data.super_likes),
      message: message.length > 0 ? message : null,
    };

    const route = '/admin/users/reward';

    if (emails.length === 0) {
      toast('Nenhum e-mail foi adicionado para o envio.', { type: 'warning' });

      return;
    }

    setLoading(true);

    try {
      let toastMessage;
      const response = await api.post(route, body);

      if (emails.length === 1) {
        toastMessage = 'Itens enviados com sucesso!';
        setEmails([]);
      } else {
        toastMessage = `Itens enviadas para ${response.data.count} com sucesso!`;
        setEmails([]);
      }

      reset();

      toast(toastMessage, { type: 'success' });
    } catch (error) {
      setErrorDialogOpen(prevState => !prevState);
      if (error.response.data.startsWith('There were')) {
        setErrorDialogTitle('It worked! But');
        // const numErrors = error.response.data.match(/\d+/)[0];
        setErrorDialogContent(
          // `${numErrors} e-mails não foram encontrados. Os itens foram adicionados aos outros e-mails`
          error.response.data
        );
      } else {
        setErrorDialogTitle('Erro ao adicionar itens');
        setErrorDialogContent('Ocorreu um erro ao adicionar os itens.');
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = data => {
    setFormData(data);
    setConfirmationDialogOpen(prevState => !prevState);
  };

  const handleCloseConfirmationDialog = () => {
    setConfirmationDialogOpen(prevState => !prevState);
  };

  return (
    <Grid container spacing={2}>
      <Grid item>
        <Title>Ucoins</Title>
      </Grid>

      <Grid xs={12} item>
        <EmailChips emailItemsGet={list => setEmails(list)} />
      </Grid>

      <Grid item xs={4}>
        <TextField
          type='number'
          variant='outlined'
          InputLabelProps={{
            shrink: true,
          }}
          id='ucoins'
          name='ucoins'
          label='Ucoins'
          fullWidth
          margin='dense'
          {...register('ucoins')}
          error={!!errors.ucoins}
        />
        <Typography variant='inherit' color='textSecondary'>
          {errors.ucoins?.message}
        </Typography>
      </Grid>

      <Grid item xs={4}>
        <TextField
          type='number'
          variant='outlined'
          InputLabelProps={{
            shrink: true,
          }}
          id='unlocks'
          name='unlocks'
          label='Unlocks'
          fullWidth
          margin='dense'
          {...register('unlocks')}
          error={!!errors.unlocks}
        />
        <Typography variant='inherit' color='textSecondary'>
          {errors.unlocks?.message}
        </Typography>
      </Grid>

      <Grid item xs={4}>
        <TextField
          type='number'
          variant='outlined'
          InputLabelProps={{
            shrink: true,
          }}
          id='super_likes'
          name='super_likes'
          label='Super Likes'
          fullWidth
          margin='dense'
          {...register('super_likes')}
          error={!!errors.super_likes}
        />
        <Typography variant='inherit' color='textSecondary'>
          {errors.super_likes?.message}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          id='message'
          name='message'
          label='Mensagem da Nina'
          fullWidth
          margin='dense'
          {...register('message')}
          multiline
          minRows={4}
          maxRows={15}
          variant='outlined'
          value={message}
          onChange={e => {
            setMessage(e.target.value);
          }}
        />
      </Grid>

      <Box m='auto' mt={3}>
        <Button disabled={loading} onClick={handleSubmit(onSubmit)} variant='contained' color='primary'>
          {loading ? 'Enviando...' : 'Enviar'}
        </Button>
      </Box>

      <Dialog
        open={confirmationDialogOpen}
        onClose={handleCloseConfirmationDialog}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>Confirmar envio dos itens?</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Após enviar esses itens, não será possível removê-los da conta do usuário. Tem certeza que deseja enviá-los?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmationDialog}>Não</Button>
          <Button
            onClick={() => {
              handleSendItems(formData);
              handleCloseConfirmationDialog();
            }}
            autoFocus
          >
            Sim
          </Button>
        </DialogActions>
      </Dialog>

      <ResponsiveDialog
        open={errorDialogOpen}
        handleClose={() => {
          setErrorDialogOpen(prevState => !prevState);
        }}
        title={errorDialogTitle}
        type='blue'
        content={errorDialogContent}
      />
    </Grid>
  );
}
